import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import {
  LogOut, Building2, MessageSquare, Calculator, Users, LayoutDashboard, Menu, X,
  Mail, BarChart3, Inbox, Palette, KeyRound, PlusCircle, Wallet, ShieldCheck, UsersRound, QrCode, Fingerprint, Globe, Megaphone, Home,
} from "lucide-react";

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const isOwner = user?.role === "owner";

  useEffect(() => {
    if (isOwner) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get("/lead-receipts", { params: { limit: 1 } });
        if (!cancelled) setUnseen(data.unseen_count || 0);
      } catch {
        if (!cancelled) setUnseen(0);
      }
    };
    refresh();
    const id = setInterval(refresh, 25000);
    return () => { cancelled = true; clearInterval(id); };
  }, [loc.pathname, isOwner]);

  const ownerNav = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/admin/agents", label: "Registered Agents", icon: UsersRound },
    { to: "/admin/ledger", label: "Subscription Ledger", icon: Wallet },
    { to: "/admin/billing-upi", label: "UPI Payments", icon: QrCode },
    { to: "/admin/ingest-project", label: "Ingest Project", icon: Fingerprint },
    { to: "/admin/builder-ingest", label: "Builder Scrape", icon: Globe },
    { to: "/admin/users", label: "Users", icon: ShieldCheck },
    { to: "/admin/listings/create", label: "Create Listing", icon: PlusCircle },
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/compliance", label: "Compliance Logs", icon: ShieldCheck },
    { to: "/admin/branding", label: "Branding", icon: Palette },
    { to: "/admin/change-password", label: "Change Password", icon: KeyRound },
  ];

  const agentNav = [
    { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
    { to: "/dashboard/projects", label: "Master Projects", icon: Building2 },
    { to: "/dashboard/listings", label: "Listings & Ingestion", icon: MessageSquare },
    { to: "/dashboard/listings/create", label: "Create Listing", icon: PlusCircle },
    { to: "/dashboard/rentals", label: "My Rentals", icon: Home },
    { to: "/dashboard/leads", label: "Leads", icon: Users },
    { to: "/dashboard/lead-receipts", label: "Lead Receipts", icon: Inbox, badge: unseen },
    { to: "/dashboard/branding", label: "Branding & Share", icon: Palette },
    { to: "/dashboard/marketing", label: "Marketing Studio", icon: Megaphone },
    { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/dashboard/calculator", label: "Acquisition Calculator", icon: Calculator },
    { to: "/dashboard/change-password", label: "Change Password", icon: KeyRound },
  ];

  const nav = isOwner ? ownerNav : agentNav;
  const baseHome = isOwner ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen bg-sg-paper text-sg-ink">
      <header className="bg-sg-navy text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden text-white/90"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-nav-toggle"
              aria-label="Toggle navigation"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to={baseHome} className="flex items-center" data-testid="brand-logo">
              <img src="/inpropportal-logo.png" alt="INPropPortal.com" className="h-11 w-auto" />
            </Link>
            {isOwner && <span className="chip chip-gold" data-testid="role-owner-badge">OWNER</span>}
          </div>

          <div className="hidden md:flex items-center gap-5">
            {!isOwner && user?.maharera_number && (
              <div className="flex flex-col items-end" data-testid="agent-identity">
                <span className="text-xs text-white/60 overline">MahaCRITI Reg No</span>
                <span className="font-mono text-sm text-sg-gold" data-testid="rera-registration-badge">{user.maharera_number}</span>
              </div>
            )}
            {!isOwner && user?.micro_market && (
              <span className="chip chip-gold" data-testid="micro-market-tag">{user.micro_market} Specialist</span>
            )}
            {!isOwner && <NotificationBell />}
            {!isOwner && user?.slug && (
              <Link to={`/agent/${user.slug}`} className="inline-flex items-center gap-2 rounded-full border border-sg-gold/70 px-4 py-2 text-sm text-sg-gold hover:bg-sg-gold hover:text-sg-navy transition-colors" data-testid="view-microsite-btn">
                <Mail className="w-4 h-4" /> My Portal
              </Link>
            )}
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/10 bg-sg-navy-deep">
            <nav className="px-4 py-3 flex flex-col gap-1" data-testid="mobile-nav-panel">
              {nav.map(({ to, label, icon: Icon, badge }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${to.split("/").slice(-1)[0] || "home"}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                    loc.pathname === to ? "bg-sg-gold/15 text-sg-gold" : "text-white/85"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{label}</span>
                  {badge ? (
                    <span className="text-[10px] font-semibold rounded-full bg-sg-gold text-sg-navy px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  ) : null}
                </Link>
              ))}
              <button
                onClick={async () => { await logout(); navigate("/login"); }}
                className="text-left mt-2 px-3 py-2 text-white/70 text-sm inline-flex items-center gap-2"
                data-testid="mobile-logout"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </nav>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8 py-10">
        <aside className="hidden lg:block">
          <nav className="flex flex-col gap-1 bg-white border border-sg-line rounded-2xl p-3">
            {nav.map(({ to, label, icon: Icon, badge }) => {
              const active = loc.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${to.split("/").slice(-1)[0] || "home"}`}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${
                    active ? "bg-sg-navy text-white" : "text-sg-navy hover:bg-sg-cream-soft"
                  }`}
                >
                  <Icon className={`w-4 h-4 ${active ? "text-sg-gold" : "text-sg-mute"}`} />
                  <span className="flex-1">{label}</span>
                  {badge ? (
                    <span className="text-[10px] font-semibold rounded-full px-1.5 min-w-[20px] h-5 inline-flex items-center justify-center bg-sg-gold text-sg-navy" data-testid={`nav-badge-${to.split("/").slice(-1)[0]}`}>
                      {badge > 99 ? "99+" : badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </nav>
        </aside>
        <main>{children}</main>
      </div>
    </div>
  );
}
